<app-modal
  [title]="'admin.customers.edit.theme.title' | transloco"
  [subtitle]="'admin.customers.edit.theme.subtitle' | transloco"
  [errorMessage]="errorMessage() || null"
  [isSubmitting]="isLoading()"
  [isDisabled]="themeForm.invalid || isLoading()"
  [confirmLabel]="'admin.customers.edit.theme.confirmLabel' | transloco"
  [submittingLabel]="'shared.actions.saving' | transloco"
  (confirm)="onSubmit()"
  (cancelled)="cancel()"
  icon="bi bi-palette"
>
  <form [formGroup]="themeForm" (ngSubmit)="onSubmit()">
    <!-- Tema -->
    <div class="row mb-4">
      <div class="col-12">
        <label for="defaultTheme" class="form-label text-secondary-brand fw-semibold">
          {{ 'admin.customers.edit.theme.applicationThemeLabel' | transloco }}
        </label>

        <ng-select
          id="defaultTheme"
          [items]="themeOptions"
          bindValue="value"
          [clearable]="false"
          formControlName="defaultTheme"
        >
          <ng-template ng-label-tmp let-item="item">
            {{ item.labelKey | transloco }}
          </ng-template>

          <ng-template ng-option-tmp let-item="item">
            {{ item.labelKey | transloco }}
          </ng-template>
        </ng-select>

        <div class="form-text text-secondary-brand">
          {{ 'admin.customers.edit.theme.applicationThemeHelp' | transloco }}
        </div>
      </div>
    </div>

    <!-- Cores personalizadas -->
    <div class="row gy-4">
      <!-- Cor primária -->
      <div class="col-12 col-lg-4">
        <label for="primaryColor" class="form-label text-secondary-brand fw-semibold">
          {{ 'admin.customers.edit.theme.primaryColorLabel' | transloco }}
        </label>

        <input
          #primaryPicker
          type="color"
          class="visually-hidden"
          [value]="getPickerValue('primaryColor')"
          (input)="setColor('primaryColor', $event)"
        />

        <div class="input-group">
          <button
            type="button"
            class="btn btn-outline-secondary d-flex align-items-center gap-2"
            [title]="'admin.customers.edit.theme.selectPrimaryColor' | transloco"
            (click)="primaryPicker.click()"
          >
            @if (themeForm.get('primaryColor')?.value) {
              <span
                class="d-inline-block flex-shrink-0 rounded border"
                [style.width.px]="18"
                [style.height.px]="18"
                [style.background-color]="themeForm.get('primaryColor')?.value"
              ></span>
            } @else {
              <i class="bi bi-eyedropper"></i>
            }
          </button>

          <input
            id="primaryColor"
            type="text"
            class="form-control"
            formControlName="primaryColor"
            [placeholder]="'admin.customers.edit.theme.noColor' | transloco"
            maxlength="20"
            [class.is-invalid]="isFieldInvalid('primaryColor')"
          />

          @if (themeForm.get('primaryColor')?.value) {
            <button
              type="button"
              class="btn btn-outline-secondary"
              [title]="'admin.customers.edit.theme.removePrimaryColor' | transloco"
              (click)="clearColor('primaryColor')"
            >
              <i class="bi bi-x-lg"></i>
            </button>
          }
        </div>

        <div class="form-text text-secondary-brand">
          {{ 'admin.customers.edit.theme.primaryColorHelp' | transloco }}
        </div>

        @if (isFieldInvalid('primaryColor')) {
          <div class="invalid-feedback d-block">
            <i class="bi bi-exclamation-circle me-1"></i>

            {{ 'admin.customers.edit.theme.invalidHexColor' | transloco }}
            <code>#0D6EFD</code>.
          </div>
        }
      </div>

      <!-- Cor secundária -->
      <div class="col-12 col-lg-4">
        <label for="secondaryColor" class="form-label text-secondary-brand fw-semibold">
          {{ 'admin.customers.edit.theme.secondaryColorLabel' | transloco }}
        </label>

        <input
          #secondaryPicker
          type="color"
          class="visually-hidden"
          [value]="getPickerValue('secondaryColor')"
          (input)="setColor('secondaryColor', $event)"
        />

        <div class="input-group">
          <button
            type="button"
            class="btn btn-outline-secondary d-flex align-items-center gap-2"
            [title]="'admin.customers.edit.theme.selectSecondaryColor' | transloco"
            (click)="secondaryPicker.click()"
          >
            @if (themeForm.get('secondaryColor')?.value) {
              <span
                class="d-inline-block flex-shrink-0 rounded border"
                [style.width.px]="18"
                [style.height.px]="18"
                [style.background-color]="themeForm.get('secondaryColor')?.value"
              ></span>
            } @else {
              <i class="bi bi-eyedropper"></i>
            }
          </button>

          <input
            id="secondaryColor"
            type="text"
            class="form-control"
            formControlName="secondaryColor"
            [placeholder]="'admin.customers.edit.theme.noColor' | transloco"
            maxlength="20"
            [class.is-invalid]="isFieldInvalid('secondaryColor')"
          />

          @if (themeForm.get('secondaryColor')?.value) {
            <button
              type="button"
              class="btn btn-outline-secondary"
              [title]="'admin.customers.edit.theme.removeSecondaryColor' | transloco"
              (click)="clearColor('secondaryColor')"
            >
              <i class="bi bi-x-lg"></i>
            </button>
          }
        </div>

        <div class="form-text text-secondary-brand">
          {{ 'admin.customers.edit.theme.secondaryColorHelp' | transloco }}
        </div>

        @if (isFieldInvalid('secondaryColor')) {
          <div class="invalid-feedback d-block">
            <i class="bi bi-exclamation-circle me-1"></i>

            {{ 'admin.customers.edit.theme.invalidHexColor' | transloco }}
            <code>#6C757D</code>.
          </div>
        }
      </div>

      <!-- Cor de destaque -->
      <div class="col-12 col-lg-4">
        <label for="accentColor" class="form-label text-secondary-brand fw-semibold">
          {{ 'admin.customers.edit.theme.accentColorLabel' | transloco }}
        </label>

        <input
          #accentPicker
          type="color"
          class="visually-hidden"
          [value]="getPickerValue('accentColor')"
          (input)="setColor('accentColor', $event)"
        />

        <div class="input-group">
          <button
            type="button"
            class="btn btn-outline-secondary d-flex align-items-center gap-2"
            [title]="'admin.customers.edit.theme.selectAccentColor' | transloco"
            (click)="accentPicker.click()"
          >
            @if (themeForm.get('accentColor')?.value) {
              <span
                class="d-inline-block flex-shrink-0 rounded border"
                [style.width.px]="18"
                [style.height.px]="18"
                [style.background-color]="themeForm.get('accentColor')?.value"
              ></span>
            } @else {
              <i class="bi bi-eyedropper"></i>
            }
          </button>

          <input
            id="accentColor"
            type="text"
            class="form-control"
            formControlName="accentColor"
            [placeholder]="'admin.customers.edit.theme.noColor' | transloco"
            maxlength="20"
            [class.is-invalid]="isFieldInvalid('accentColor')"
          />

          @if (themeForm.get('accentColor')?.value) {
            <button
              type="button"
              class="btn btn-outline-secondary"
              [title]="'admin.customers.edit.theme.removeAccentColor' | transloco"
              (click)="clearColor('accentColor')"
            >
              <i class="bi bi-x-lg"></i>
            </button>
          }
        </div>

        <div class="form-text text-secondary-brand">{{ 'admin.customers.edit.theme.accentColorHelp' | transloco }}</div>

        @if (isFieldInvalid('accentColor')) {
          <div class="invalid-feedback d-block">
            <i class="bi bi-exclamation-circle me-1"></i>

            {{ 'admin.customers.edit.theme.invalidHexColor' | transloco }}
            <code>#0D6EFD</code>.
          </div>
        }
      </div>
    </div>

    <!-- Pré-visualização -->
    <div class="border-default rounded p-3 mt-4">
      <div class="d-flex align-items-center justify-content-between mb-3">
        <strong class="text-primary-brand"> {{ 'admin.customers.edit.theme.previewTitle' | transloco }} </strong>

        <span class="badge bg-secondary">
          {{ getThemeLabel() }}
        </span>
      </div>

      <div
        class="border-default rounded p-4"
        [style.background-color]="getPreviewBackgroundColor()"
        [style.color]="getPreviewTextColor()"
      >
        <!-- Cabeçalho -->
        <div class="d-flex flex-column flex-md-row align-items-md-center justify-content-between gap-2 mb-4">
          <div>
            <h5
              class="mb-1"
              [style.--preview-title-color]="getPreviewTitleColor()"
              style="color: var(--preview-title-color) !important"
            >
              {{ 'admin.customers.edit.theme.previewAppTitle' | transloco }}
            </h5>

            <p class="mb-0" [style.color]="getSecondaryColor()">
              {{ 'admin.customers.edit.theme.previewAppSubtitle' | transloco }}
            </p>
          </div>

          <a
            href="#"
            class="text-decoration-none fw-semibold"
            [style.color]="getPrimaryColor()"
            (click)="$event.preventDefault()"
          >
            {{ 'admin.customers.edit.theme.previewDetailsLink' | transloco }}

            <i class="bi bi-arrow-right ms-1"></i>
          </a>
        </div>

        <!-- Botões e badge -->
        <div class="d-flex flex-wrap align-items-center gap-2 mb-4">
          <button
            type="button"
            class="btn"
            [style.background-color]="getPrimaryColor()"
            [style.border-color]="getPrimaryColor()"
            [style.color]="getContrastColor(getPrimaryColor())"
          >
            <i class="bi bi-check-circle me-2"></i>

            {{ 'admin.customers.edit.theme.previewPrimaryButton' | transloco }}
          </button>

          <button
            type="button"
            class="btn"
            [style.background-color]="'transparent'"
            [style.border-color]="getSecondaryColor()"
            [style.color]="getSecondaryColor()"
          >
            <i class="bi bi-arrow-left me-2"></i>

            {{ 'admin.customers.edit.theme.previewSecondaryButton' | transloco }}
          </button>

          <span
            class="badge"
            [style.background-color]="getAccentColor()"
            [style.color]="getContrastColor(getAccentColor())"
          >
            {{ 'admin.customers.edit.theme.previewBadge' | transloco }}
          </span>
        </div>

        <!-- Links -->
        <div class="d-flex flex-wrap gap-3 mb-4">
          <a
            href="#"
            class="text-decoration-none fw-semibold"
            [style.color]="getPrimaryColor()"
            (click)="$event.preventDefault()"
          >
            {{ 'admin.customers.edit.theme.previewPrimaryLink' | transloco }}
          </a>

          <a
            href="#"
            class="text-decoration-underline"
            [style.color]="getSecondaryColor()"
            (click)="$event.preventDefault()"
          >
            {{ 'admin.customers.edit.theme.previewSecondaryLink' | transloco }}
          </a>
        </div>

        <!-- Cards -->
        <div class="row g-3">
          <div class="col-12 col-md-6">
            <div
              class="h-100 rounded p-3"
              [style.background-color]="getPreviewSurfaceColor()"
              [style.border]="'1px solid ' + getPrimaryColor()"
            >
              <div class="d-flex align-items-center justify-content-between mb-3">
                <span
                  class="d-inline-flex align-items-center justify-content-center rounded"
                  [style.width.px]="40"
                  [style.height.px]="40"
                  [style.background-color]="getAccentColor()"
                  [style.color]="getContrastColor(getAccentColor())"
                >
                  <i class="bi bi-bar-chart"></i>
                </span>

                <span
                  class="badge"
                  [style.background-color]="getAccentColor()"
                  [style.color]="getContrastColor(getAccentColor())"
                >
                  {{ 'shared.status.active' | transloco }}
                </span>
              </div>

              <h6
                [style.--preview-title-color]="getPreviewTitleColor()"
                style="color: var(--preview-title-color) !important"
              >
                {{ 'admin.customers.edit.theme.previewMonthlyReport' | transloco }}
              </h6>

              <p class="mb-3" [style.color]="getSecondaryColor()">
                {{ 'admin.customers.edit.theme.previewThemeCardText' | transloco }}
              </p>

              <a
                href="#"
                class="text-decoration-none fw-semibold"
                [style.color]="getPrimaryColor()"
                (click)="$event.preventDefault()"
              >
                {{ 'admin.customers.edit.theme.previewOpenReport' | transloco }}
              </a>
            </div>
          </div>

          <div class="col-12 col-md-6">
            <div
              class="h-100 rounded p-3"
              [style.background-color]="getPreviewSurfaceColor()"
              [style.border]="'1px solid ' + getAccentColor()"
            >
              <div class="d-flex align-items-center justify-content-between mb-3">
                <span
                  class="d-inline-flex align-items-center justify-content-center rounded"
                  [style.width.px]="40"
                  [style.height.px]="40"
                  [style.background-color]="getPrimaryColor()"
                  [style.color]="getContrastColor(getPrimaryColor())"
                >
                  <i class="bi bi-folder"></i>
                </span>

                <i class="bi bi-three-dots" [style.color]="getSecondaryColor()"></i>
              </div>

              <h6
                [style.--preview-title-color]="getPreviewTitleColor()"
                style="color: var(--preview-title-color) !important"
              >
                {{ 'admin.customers.edit.theme.previewProjectInProgress' | transloco }}
              </h6>

              <p class="mb-3" [style.color]="getSecondaryColor()">
                {{ 'admin.customers.edit.theme.previewContrastCardText' | transloco }}
              </p>

              <button
                type="button"
                class="btn btn-sm"
                [style.background-color]="'transparent'"
                [style.border-color]="getPrimaryColor()"
                [style.color]="getPrimaryColor()"
              >
                {{ 'admin.customers.edit.theme.previewAccessProject' | transloco }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Restaurar -->
    <div class="mt-3">
      <button type="button" class="btn btn-outline-secondary" [disabled]="isLoading()" (click)="resetThemeForm()">
        <i class="bi bi-arrow-counterclockwise me-2"></i>

        {{ 'admin.customers.edit.theme.restoreDefaults' | transloco }}
      </button>
    </div>
  </form>
</app-modal>
